// client/src/components/HOME/LegalPage.js

import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { FiArrowLeft } from "react-icons/fi";
import { BrandMark } from "../shared/BrandLogo";

const DOCUMENTS = {
  "mentions-legales": { title: "Mentions légales", file: "mentions-legales.md" },
  "cgu": { title: "Conditions générales d'utilisation", file: "conditions-generales-utilisation.md" },
  "cgv": { title: "Conditions générales de vente", file: "conditions-generales-vente.md" },
  "conditions-affilies": { title: "Conditions affiliés", file: "conditions-affilies.md" },
  "politique-confidentialite": { title: "Politique de confidentialité", file: "politique-confidentialite-rgpd.md" },
  "politique-cookies": { title: "Politique de cookies", file: "politique-cookies.md" },
};

function renderMarkdown(md) {
  const lines = md.split("\n");
  const elements = [];
  let i = 0;

  const parseInline = (text) => {
    // Bold + inline code
    const parts = text.split(/(`[^`]+`|\*\*[^*]+\*\*)/g);
    return parts.map((part, idx) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return <strong key={idx}>{part.slice(2, -2)}</strong>;
      }
      if (part.startsWith("`") && part.endsWith("`")) {
        return <code key={idx} style={{ background: "var(--eb-surface-2)", border: "1px solid var(--eb-border)", borderRadius: 4, padding: "1px 5px", fontFamily: "var(--eb-font-mono)", fontSize: "0.92em" }}>{part.slice(1, -1)}</code>;
      }
      // Convert URLs to links
      const urlRegex = /(https?:\/\/[^\s)]+)/g;
      if (urlRegex.test(part)) {
        const urlParts = part.split(/(https?:\/\/[^\s)]+)/g);
        return urlParts.map((up, uidx) =>
          /^https?:\/\//.test(up)
            ? <a key={uidx} href={up} target="_blank" rel="noopener noreferrer" style={{ color: "var(--eb-brand)", wordBreak: "break-all" }}>{up}</a>
            : up
        );
      }
      return part;
    });
  };

  while (i < lines.length) {
    const line = lines[i];

    // Horizontal rule
    if (/^---+$/.test(line.trim())) {
      elements.push(<hr key={i} style={{ border: "none", borderTop: "1px solid var(--eb-border)", margin: "24px 0" }} />);
      i++;
      continue;
    }

    // Table
    if (line.includes("|") && lines[i + 1] && /^\|[-| :]+\|/.test(lines[i + 1])) {
      const tableLines = [];
      while (i < lines.length && lines[i].includes("|")) {
        tableLines.push(lines[i]);
        i++;
      }
      const headers = tableLines[0].split("|").filter((_, ci) => ci > 0 && ci < tableLines[0].split("|").length - 1);
      const rows = tableLines.slice(2).map(row => row.split("|").filter((_, ci) => ci > 0 && ci < row.split("|").length - 1));
      elements.push(
        <div key={`table-${i}`} style={{ overflowX: "auto", margin: "16px 0" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.93em" }}>
            <thead>
              <tr style={{ background: "var(--eb-surface-2)" }}>
                {headers.map((h, hi) => (
                  <th key={hi} style={{ padding: "8px 12px", border: "1px solid var(--eb-border)", textAlign: "left", fontWeight: 600, color: "var(--eb-text-2)", fontSize: 13 }}>{parseInline(h.trim())}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, ri) => (
                <tr key={ri} style={{ background: ri % 2 === 0 ? "var(--eb-surface)" : "var(--eb-surface-2)" }}>
                  {row.map((cell, ci) => (
                    <td key={ci} style={{ padding: "7px 12px", border: "1px solid var(--eb-border)", verticalAlign: "top" }}>{parseInline(cell.trim())}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    // Headings
    if (line.startsWith("#### ")) {
      elements.push(<h4 key={i} style={{ fontSize: "1em", fontWeight: 600, margin: "18px 0 6px", color: "var(--eb-text)" }}>{parseInline(line.slice(5))}</h4>);
    } else if (line.startsWith("### ")) {
      elements.push(<h3 key={i} style={{ fontSize: "1.1em", fontWeight: 650, margin: "24px 0 8px", color: "var(--eb-text)" }}>{parseInline(line.slice(4))}</h3>);
    } else if (line.startsWith("## ")) {
      elements.push(<h2 key={i} style={{ fontSize: "1.3em", fontWeight: 700, margin: "36px 0 12px", color: "var(--eb-brand-800)", borderBottom: "1px solid var(--eb-border)", paddingBottom: 8 }}>{parseInline(line.slice(3))}</h2>);
    } else if (line.startsWith("# ")) {
      elements.push(<h1 key={i} style={{ fontSize: "clamp(1.6rem, 4vw, 2.1rem)", fontWeight: 700, margin: "0 0 20px", color: "var(--eb-text)", letterSpacing: "-0.02em", lineHeight: 1.2 }}>{parseInline(line.slice(2))}</h1>);

    // List item
    } else if (/^[-*] /.test(line)) {
      const listItems = [];
      while (i < lines.length && /^[-*] /.test(lines[i])) {
        listItems.push(<li key={i} style={{ marginBottom: 6, lineHeight: 1.65 }}>{parseInline(lines[i].slice(2))}</li>);
        i++;
      }
      elements.push(<ul key={`ul-${i}`} style={{ margin: "8px 0 8px 22px", padding: 0 }}>{listItems}</ul>);
      continue;

    // Empty line
    } else if (line.trim() === "") {
      elements.push(<div key={i} style={{ height: 8 }} />);

    // Normal paragraph
    } else {
      elements.push(<p key={i} style={{ margin: "6px 0", lineHeight: 1.65 }}>{parseInline(line)}</p>);
    }

    i++;
  }

  return elements;
}

export default function LegalPage() {
  const { docId } = useParams();
  const doc = DOCUMENTS[docId];
  const [content, setContent] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!doc) { setError(true); return; }
    fetch(`${process.env.PUBLIC_URL}/legal/${doc.file}`)
      .then((res) => {
        if (!res.ok) throw new Error("not found");
        return res.text();
      })
      .then(setContent)
      .catch(() => setError(true));
  }, [doc]);

  return (
    <div style={{ minHeight: "100vh", background: "var(--eb-bg)", display: "flex", flexDirection: "column" }}>
      <header
        style={{
          position: "sticky", top: 0, zIndex: 10,
          background: "rgba(255,255,255,0.92)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
          borderBottom: "1px solid var(--eb-border)",
        }}
      >
        <div
          style={{
            maxWidth: 880, margin: "0 auto", height: 64, padding: "0 20px",
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
          }}
        >
          <BrandMark size={32} />
          <Link to="/" className="eb-btn eb-btn--ghost eb-btn--sm" style={{ minHeight: 40 }}>
            <FiArrowLeft aria-hidden="true" /> Retour à l'accueil
          </Link>
        </div>
      </header>

      <main style={{ maxWidth: 880, margin: "0 auto", padding: "32px 16px 72px", width: "100%", boxSizing: "border-box" }}>
        {error && (
          <div className="eb-card eb-card--pad" style={{ textAlign: "center" }}>
            <p style={{ margin: 0, color: "var(--eb-danger)", fontWeight: 600 }}>Document introuvable.</p>
          </div>
        )}
        {!error && content === null && (
          <div className="eb-card eb-card--pad" style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 12, color: "var(--eb-muted)" }}>
            <span className="eb-spinner eb-spinner--sm" aria-hidden="true" /> Chargement…
          </div>
        )}
        {!error && content !== null && (
          <article
            className="eb-card"
            style={{ padding: "clamp(22px, 5vw, 48px)", fontSize: 15, color: "var(--eb-text-2)", overflowWrap: "anywhere" }}
          >
            {renderMarkdown(content)}
          </article>
        )}
      </main>
    </div>
  );
}
